import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { groups } from '../data/mockData';

const CreatePostContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PageDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  background-color: ${({ theme }) => theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, checked }) => checked ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background-color: ${({ theme, checked }) => checked ? `${theme.colors.primary}10` : theme.colors.background};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  input {
    margin-right: ${({ theme }) => theme.spacing.sm};
  }
`;

const MediaPreviewContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 300px;
  background-color: ${({ theme }) => theme.colors.cardBg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img, video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const MediaUploadContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const UploadText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 50px;
  
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.cardBg};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const TagText = styled.span`
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

const RemoveTagButton = styled.button`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 100px;
  border: none;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  
  &:focus {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const Button = styled.button`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.cardBg};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const SubmitButton = styled(Button)`
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const CarouselPreview = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  overflow-x: auto;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.cardBg};
    border-radius: ${({ theme }) => theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.text.tertiary};
    border-radius: ${({ theme }) => theme.borderRadius.full};
  }
`;

const CarouselItem = styled.div`
  position: relative;
  min-width: 150px;
  height: 150px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveMediaButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.xs};
  right: ${({ theme }) => theme.spacing.xs};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const CreatePostPage = () => {
  const navigate = useNavigate();
  const [postType, setPostType] = useState('text');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const handlePostTypeChange = (type) => {
    setPostType(type);
    // Clear media files when changing post type
    if (type === 'text') {
      setMediaFiles([]);
    }
  };

  const handleMediaUpload = (e) => {
    const files = Array.from(e.target.files);

    if (postType === 'carousel' || postType === 'reels') {
      // Add to existing files for carousel or reels
      setMediaFiles(prev => [...prev, ...files]);
    } else {
      // Replace existing file for image or video
      setMediaFiles(files);
    }
  };

  const handleRemoveMedia = (index) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      if (!tags.includes(tagInput.trim())) {
        setTags(prev => [...prev, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!content.trim()) {
      newErrors.content = 'Content is required';
    }

    if (!selectedGroup) {
      newErrors.group = 'Please select a group';
    }

    if ((postType === 'image' || postType === 'video') && mediaFiles.length === 0) {
      newErrors.media = 'Please upload media';
    }

    if (postType === 'carousel' && mediaFiles.length < 2) {
      newErrors.media = 'Please upload at least 2 images for carousel';
    }

    if (postType === 'reels' && mediaFiles.length === 0) {
      newErrors.media = 'Please upload a video for reels';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // In a real app, this would send the post data to the server
      alert(`Post created: ${title}`);
      navigate('/');
    }
  };

  const renderMediaUpload = () => {
    switch (postType) {
      case 'image':
        return (
          <FormGroup>
            <Label>Upload Image</Label>
            <MediaUploadContainer>
              <input
                type="file"
                accept="image/*"
                onChange={handleMediaUpload}
              />
              <UploadIcon>🖼️</UploadIcon>
              <UploadText>Click to upload an image</UploadText>
            </MediaUploadContainer>
            {errors.media && <ErrorMessage>{errors.media}</ErrorMessage>}

            {mediaFiles.length > 0 && (
              <MediaPreviewContainer>
                <Label>Preview</Label>
                <MediaPreview>
                  <img
                    src={URL.createObjectURL(mediaFiles[0])}
                    alt="Preview"
                  />
                </MediaPreview>
              </MediaPreviewContainer>
            )}
          </FormGroup>
        );

      case 'video':
        return (
          <FormGroup>
            <Label>Upload Video</Label>
            <MediaUploadContainer>
              <input
                type="file"
                accept="video/*"
                onChange={handleMediaUpload}
              />
              <UploadIcon>🎬</UploadIcon>
              <UploadText>Click to upload a video</UploadText>
            </MediaUploadContainer>
            {errors.media && <ErrorMessage>{errors.media}</ErrorMessage>}

            {mediaFiles.length > 0 && (
              <MediaPreviewContainer>
                <Label>Preview</Label>
                <MediaPreview>
                  <video
                    src={URL.createObjectURL(mediaFiles[0])}
                    controls
                  />
                </MediaPreview>
              </MediaPreviewContainer>
            )}
          </FormGroup>
        );

      case 'carousel':
        return (
          <FormGroup>
            <Label>Upload Images for Carousel</Label>
            <MediaUploadContainer>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleMediaUpload}
              />
              <UploadIcon>📱</UploadIcon>
              <UploadText>Click to upload multiple images</UploadText>
            </MediaUploadContainer>
            {errors.media && <ErrorMessage>{errors.media}</ErrorMessage>}

            {mediaFiles.length > 0 && (
              <MediaPreviewContainer>
                <Label>Preview</Label>
                <CarouselPreview>
                  {mediaFiles.map((file, index) => (
                    <CarouselItem key={index}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Carousel item ${index + 1}`}
                      />
                      <RemoveMediaButton onClick={() => handleRemoveMedia(index)}>
                        ✕
                      </RemoveMediaButton>
                    </CarouselItem>
                  ))}
                </CarouselPreview>
              </MediaPreviewContainer>
            )}
          </FormGroup>
        );

      case 'reels':
        return (
          <FormGroup>
            <Label>Upload Short Video for Reels</Label>
            <MediaUploadContainer>
              <input
                type="file"
                accept="video/*"
                onChange={handleMediaUpload}
              />
              <UploadIcon>📱</UploadIcon>
              <UploadText>Click to upload a short video</UploadText>
            </MediaUploadContainer>
            {errors.media && <ErrorMessage>{errors.media}</ErrorMessage>}

            {mediaFiles.length > 0 && (
              <MediaPreviewContainer>
                <Label>Preview</Label>
                <MediaPreview>
                  <video
                    src={URL.createObjectURL(mediaFiles[0])}
                    controls
                  />
                </MediaPreview>
              </MediaPreviewContainer>
            )}
          </FormGroup>
        );

      default:
        return null;
    }
  };

  return (
    <CreatePostContainer>
      <PageHeader>
        <PageTitle>Create Post</PageTitle>
        <PageDescription>
          Share your thoughts, media, or create a discussion with the community.
        </PageDescription>
      </PageHeader>

      <Card>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Post Type</Label>
            <RadioGroup>
              <RadioOption checked={postType === 'text'}>
                <input
                  type="radio"
                  name="postType"
                  value="text"
                  checked={postType === 'text'}
                  onChange={() => handlePostTypeChange('text')}
                />
                Text
              </RadioOption>
              <RadioOption checked={postType === 'image'}>
                <input
                  type="radio"
                  name="postType"
                  value="image"
                  checked={postType === 'image'}
                  onChange={() => handlePostTypeChange('image')}
                />
                Image
              </RadioOption>
              <RadioOption checked={postType === 'video'}>
                <input
                  type="radio"
                  name="postType"
                  value="video"
                  checked={postType === 'video'}
                  onChange={() => handlePostTypeChange('video')}
                />
                Video
              </RadioOption>
              <RadioOption checked={postType === 'carousel'}>
                <input
                  type="radio"
                  name="postType"
                  value="carousel"
                  checked={postType === 'carousel'}
                  onChange={() => handlePostTypeChange('carousel')}
                />
                Carousel Gallery
              </RadioOption>
              <RadioOption checked={postType === 'reels'}>
                <input
                  type="radio"
                  name="postType"
                  value="reels"
                  checked={postType === 'reels'}
                  onChange={() => handlePostTypeChange('reels')}
                />
                Reels (Short Video)
              </RadioOption>
            </RadioGroup>
          </FormGroup>

          <FormGroup>
            <Label>Title</Label>
            <Input
              type="text"
              placeholder="Enter a title for your post"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
          </FormGroup>

          {renderMediaUpload()}

          <FormGroup>
            <Label>Content</Label>
            <Textarea
              placeholder="Write your post content here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            {errors.content && <ErrorMessage>{errors.content}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Select Group</Label>
            <Select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
            >
              <option value="">Select a group</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            {errors.group && <ErrorMessage>{errors.group}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Tags</Label>
            <TagsInput>
              {tags.map((tag, index) => (
                <Tag key={index}>
                  <TagText>{tag}</TagText>
                  <RemoveTagButton onClick={() => handleRemoveTag(tag)}>
                    ✕
                  </RemoveTagButton>
                </Tag>
              ))}
              <TagInput
                type="text"
                placeholder={tags.length === 0 ? "Add tags (press Enter after each tag)" : ""}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
              />
            </TagsInput>
          </FormGroup>

          <ButtonGroup>
            <CancelButton type="button" onClick={() => navigate('/')}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit">
              Create Post
            </SubmitButton>
          </ButtonGroup>
        </form>
      </Card>
    </CreatePostContainer>
  );
};

export default CreatePostPage;
